import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useUpdateRecitalMutation } from '~entities/recital-manager/api/update-recital';

import { Button } from '~/shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import { Textarea } from '~/shared/shadcn/ui/textarea';

const recitalFormSchema = z.object({
  name: z.string().min(1, 'Recital name is required'),
  year: z.coerce.number().min(2020).max(2100),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  description: z.string().optional(),
  allowParentSizing: z.boolean(),
  allowVolunteerSignup: z.boolean(),
  checkInEnabled: z.boolean(),
  autoNotifyOnPublish: z.boolean(),
});

export type RecitalFormValues = z.infer<typeof recitalFormSchema>;

interface EditRecitalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recitalId: string;
  recital: {
    name: string;
    year: number;
    startDate: string;
    endDate: string;
    description?: string;
    settings: {
      allowParentSizing: boolean;
      allowVolunteerSignup: boolean;
      checkInEnabled: boolean;
      autoNotifyOnPublish: boolean;
    };
  };
}

export const EditRecitalDialog = ({
  open,
  onOpenChange,
  recitalId,
  recital,
}: EditRecitalDialogProps) => {
  const { mutateAsync: updateRecital, isPending: isUpdatingRecital } =
    useUpdateRecitalMutation();

  const form = useForm<RecitalFormValues>({
    resolver: zodResolver(recitalFormSchema),
    defaultValues: {
      name: recital.name,
      year: recital.year,
      startDate: recital.startDate.split('T')[0],
      endDate: recital.endDate.split('T')[0],
      description: recital.description ?? '',
      allowParentSizing: recital.settings.allowParentSizing,
      allowVolunteerSignup: recital.settings.allowVolunteerSignup,
      checkInEnabled: recital.settings.checkInEnabled,
      autoNotifyOnPublish: recital.settings.autoNotifyOnPublish,
    },
  });

  const handleUpdate = async (values: RecitalFormValues) => {
    try {
      await updateRecital({
        recitalId,
        name: values.name,
        year: values.year,
        startDate: values.startDate,
        endDate: values.endDate,
        description: values.description,
        settings: {
          allowParentSizing: values.allowParentSizing,
          allowVolunteerSignup: values.allowVolunteerSignup,
          checkInEnabled: values.checkInEnabled,
          autoNotifyOnPublish: values.autoNotifyOnPublish,
        },
      });
      toast.success('Recital updated successfully!');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update recital:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Edit Recital</DialogTitle>
          <DialogDescription>
            Update the recital details and settings below.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleUpdate)}
            className='space-y-6'
          >
            <div className='grid grid-cols-2 gap-4'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='col-span-2'>
                    <FormLabel>Recital Name</FormLabel>
                    <FormControl>
                      <Input placeholder='Spring Recital 2024' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='year'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year</FormLabel>
                    <FormControl>
                      <Input type='number' placeholder='2024' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='startDate'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type='date' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='endDate'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type='date' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='Add a description for this recital...'
                      className='resize-none'
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
              
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isUpdatingRecital}>
                {isUpdatingRecital && <Loader2 className='animate-spin' />}
                Update
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
